// Server settings → Channels: every text channel by category. Rename a channel, set its topic, move it (drag on
// desktop; up/down and "Move to…" everywhere), delete it; rename, reorder and delete categories. DMs never appear.
// Every change goes through the room, which sends the result to everyone (this list included), so nothing here is
// optimistic: the room answers in milliseconds and the list just follows the snapshot.
import { useEffect, useRef, useState } from "react";
import { CARDS_PER_MINUTE, MAX_TOPIC_LENGTH, type AppManifest, type BotInfo, type Category, type Channel } from "@bloom/shared";
import type { Room } from "../realtime.ts";
import { Confirm } from "./Confirm.tsx";
import "./server-settings.css";

type Drag = { kind: "channel" | "category"; id: string };
/** Where a drop would land: before or after a row, or into an (empty) category. */
type DropAt = { kind: "channel"; id: string; after: boolean } | { kind: "category"; id: string | null; after: boolean };

const Up = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m18 15-6-6-6 6" /></svg>;
const Down = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>;
const Grip = () => <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true"><circle cx="3" cy="3" r="1.4" /><circle cx="9" cy="3" r="1.4" /><circle cx="3" cy="8" r="1.4" /><circle cx="9" cy="8" r="1.4" /><circle cx="3" cy="13" r="1.4" /><circle cx="9" cy="13" r="1.4" /></svg>;

export function ChannelSettings({ room, channels, categories, focusId, apps = [], bots = [], names = new Map() }: { room: Room | null; channels: Channel[]; categories: Category[]; focusId?: string; apps?: AppManifest[]; bots?: BotInfo[]; names?: Map<string, string> }) {
  const text = channels.filter((c) => c.kind === "text");
  const cats = [...categories].sort((a, b) => a.position - b.position);
  const inCat = (id: string | null) => text.filter((c) => c.categoryId === id).sort((a, b) => a.position - b.position);
  const groups: { cat: Category | null; list: Channel[] }[] = [{ cat: null, list: inCat(null) }, ...cats.map((cat) => ({ cat, list: inCat(cat.id) }))];

  const [open, setOpen] = useState<string | null>(focusId && text.some((c) => c.id === focusId) ? focusId : null);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [dropAt, setDropAt] = useState<DropAt | null>(null);
  const [renamingCat, setRenamingCat] = useState<{ id: string; name: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "channel"; channel: Channel } | { kind: "category"; category: Category; count: number } | null>(null);

  // Opened from a channel's "Edit channel" (or that link loaded fresh, before the channels arrived): open it once
  // it's there, and bring it into view.
  const focused = useRef(false);
  const focusReady = !!focusId && text.some((c) => c.id === focusId);
  useEffect(() => {
    if (!focusReady || focused.current) return;
    focused.current = true;
    setOpen(focusId!);
    requestAnimationFrame(() => document.getElementById(`chset-${focusId}`)?.scrollIntoView({ block: "center" }));
  }, [focusId, focusReady]);

  const report = (r: { ok: true } | { ok: false; message: string } | undefined) => setError(r && !r.ok ? r.message : null);
  const moveChannel = async (c: Channel, categoryId: string | null, position: number) => report(await room?.moveChannel(c.id, categoryId, position));
  const moveCategory = async (id: string, position: number) => report(await room?.moveCategory(id, position));

  /** Up or down one place; past the top or bottom of a category, into the neighbouring one. */
  const step = (c: Channel, dir: -1 | 1) => {
    const g = groups.findIndex((x) => (x.cat?.id ?? null) === c.categoryId);
    const list = groups[g]!.list;
    const i = list.findIndex((x) => x.id === c.id);
    if (i + dir >= 0 && i + dir < list.length) return moveChannel(c, c.categoryId, i + dir);
    const next = groups[g + dir];
    if (next) return moveChannel(c, next.cat?.id ?? null, dir === 1 ? 0 : next.list.length);
  };

  const onDrop = () => {
    const d = drag;
    const at = dropAt;
    setDrag(null);
    setDropAt(null);
    if (!d || !at) return;
    if (d.kind === "category") {
      // Categories only land among categories.
      const targetCat = at.kind === "category" ? at.id : text.find((c) => c.id === at.id)?.categoryId ?? null;
      if (!targetCat || targetCat === d.id) return;
      const order = cats.map((c) => c.id).filter((id) => id !== d.id);
      const i = order.indexOf(targetCat);
      return moveCategory(d.id, at.kind === "category" && at.after ? i + 1 : at.kind === "category" ? i : i + 1);
    }
    const c = text.find((x) => x.id === d.id);
    if (!c) return;
    if (at.kind === "category") {
      // On a category's header: first in it (or, for the "no category" header, first there).
      return moveChannel(c, at.id, 0);
    }
    const target = text.find((x) => x.id === at.id);
    if (!target || target.id === c.id) return;
    const list = inCat(target.categoryId).filter((x) => x.id !== c.id);
    const i = list.findIndex((x) => x.id === target.id);
    return moveChannel(c, target.categoryId, at.after ? i + 1 : i);
  };

  const dropProps = (at: (after: boolean) => DropAt) => ({
    onDragOver: (e: React.DragEvent) => {
      if (!drag) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      const box = e.currentTarget.getBoundingClientRect();
      const next = at(e.clientY > box.top + box.height / 2);
      if (JSON.stringify(next) !== JSON.stringify(dropAt)) setDropAt(next);
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      onDrop();
    },
  });
  const dragProps = (d: Drag) => ({
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", d.id); // Firefox won't start a drag without data
      setDrag(d);
    },
    onDragEnd: () => {
      setDrag(null);
      setDropAt(null);
    },
  });
  const marker = (kind: DropAt["kind"], id: string | null) =>
    dropAt && dropAt.kind === kind && dropAt.id === id ? (dropAt.after ? " drop-after" : " drop-before") : "";

  return (
    <div className="chset" onDragOver={(e) => drag && e.preventDefault()}>
      <p className="setting-help" style={{ margin: "0 0 8px" }}>
        <span className="desktop-only">Drag channels and categories to reorder them.</span> Changes show up for everyone right away.
      </p>
      {error && <p className="error" role="alert">{error}</p>}
      {groups.map(({ cat, list }, gi) => {
        const catId = cat?.id ?? null;
        if (!cat && !list.length && !drag) return null;
        return (
          <section key={catId ?? "none"} className={`chset-group${drag?.kind === "category" && drag.id === catId ? " dragging" : ""}`} aria-label={cat ? cat.name : "No category"}>
            <div
              className={`chset-cat${drag?.kind === "category" ? marker("category", catId) : drag?.kind === "channel" && dropAt?.kind === "category" && dropAt.id === catId ? " drop-into" : ""}`}
              {...dropProps((after) => ({ kind: "category", id: catId, after }))}
            >
              {cat && (
                <span className="chset-grip desktop-only" title="Drag to reorder" {...dragProps({ kind: "category", id: cat.id })}>
                  <Grip />
                </span>
              )}
              {cat && renamingCat?.id === cat.id ? (
                <form
                  className="chset-cat-rename"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const name = renamingCat.name.trim();
                    if (!name || name === cat.name) return setRenamingCat(null);
                    const r = await room?.renameCategory(cat.id, name);
                    report(r);
                    if (r?.ok) setRenamingCat(null);
                  }}
                >
                  <input className="input chset-input" aria-label="Category name" autoFocus maxLength={50} value={renamingCat.name} onChange={(e) => setRenamingCat({ id: cat.id, name: e.target.value })} onKeyDown={(e) => e.key === "Escape" && (e.preventDefault(), setRenamingCat(null))} />
                  <button className="btn btn-small btn-primary" type="submit">Save</button>
                </form>
              ) : (
                <span className="chset-cat-name">{cat ? cat.name : "No category"}</span>
              )}
              {cat && renamingCat?.id !== cat.id && (
                <span className="chset-cat-actions">
                  <button type="button" className="icon-btn" aria-label={`Move ${cat.name} up`} title="Move up" disabled={gi <= 1} onClick={() => moveCategory(cat.id, gi - 2)}><Up /></button>
                  <button type="button" className="icon-btn" aria-label={`Move ${cat.name} down`} title="Move down" disabled={gi === groups.length - 1} onClick={() => moveCategory(cat.id, gi)}><Down /></button>
                  <button type="button" className="link-btn chset-link" onClick={() => setRenamingCat({ id: cat.id, name: cat.name })}>Rename</button>
                  <button type="button" className="link-btn chset-link danger" onClick={() => setConfirm({ kind: "category", category: cat, count: list.length })}>Delete</button>
                </span>
              )}
            </div>
            {list.length === 0 && <div className="chset-empty muted" {...dropProps(() => ({ kind: "category", id: catId, after: false }))}>No channels</div>}
            {list.map((c, i) => (
              <div key={c.id} id={`chset-${c.id}`} className={`chset-item${open === c.id ? " open" : ""}${drag?.id === c.id ? " dragging" : ""}`}>
                <div className={`chset-row${drag?.kind === "channel" ? marker("channel", c.id) : ""}`} {...dropProps((after) => ({ kind: "channel", id: c.id, after }))}>
                  <span className="chset-grip desktop-only" title="Drag to reorder" {...dragProps({ kind: "channel", id: c.id })}>
                    <Grip />
                  </span>
                  <button type="button" className="chset-main" aria-expanded={open === c.id} onClick={() => setOpen(open === c.id ? null : c.id)}>
                    <span className="chset-name"><span className="muted">#</span> {c.name}</span>
                    {c.topic && <span className="chset-topic">{c.topic}</span>}
                  </button>
                  <span className="chset-steps">
                    <button type="button" className="icon-btn" aria-label={`Move #${c.name} up`} title="Move up" disabled={gi === 0 && i === 0} onClick={() => step(c, -1)}><Up /></button>
                    <button type="button" className="icon-btn" aria-label={`Move #${c.name} down`} title="Move down" disabled={gi === groups.length - 1 && i === list.length - 1} onClick={() => step(c, 1)}><Down /></button>
                  </span>
                </div>
                {open === c.id && (
                  <ChannelEditor
                    key={c.id}
                    room={room}
                    channel={c}
                    categories={cats}
                    apps={apps}
                    bots={bots}
                    names={names}
                    onMove={(categoryId) => moveChannel(c, categoryId, inCat(categoryId).length)}
                    onDelete={() => setConfirm({ kind: "channel", channel: c })}
                  />
                )}
              </div>
            ))}
          </section>
        );
      })}
      {confirm?.kind === "channel" && (
        <Confirm
          title={`Delete #${confirm.channel.name}?`}
          confirmLabel="Delete channel"
          danger
          typeToConfirm={confirm.channel.name}
          onConfirm={async () => {
            const r = await room?.deleteChannel(confirm.channel.id);
            return r?.ok ? null : (r?.message ?? "Couldn't delete it.");
          }}
          onClose={() => setConfirm(null)}
        >
          Every message, thread, reaction and file in it is deleted for everyone. This can't be undone.
        </Confirm>
      )}
      {confirm?.kind === "category" && (
        <Confirm
          title={`Delete ${confirm.category.name}?`}
          confirmLabel="Delete category"
          danger
          onConfirm={async () => {
            const r = await room?.deleteCategory(confirm.category.id);
            return r?.ok ? null : (r?.message ?? "Couldn't delete it.");
          }}
          onClose={() => setConfirm(null)}
        >
          {confirm.count ? `Its ${confirm.count === 1 ? "channel stays" : `${confirm.count} channels stay`}, without a category.` : "It has no channels."}
        </Confirm>
      )}
    </div>
  );
}

/** Name, topic, category and delete for one channel. Name and topic save together. */
function ChannelEditor({ room, channel, categories, apps, bots, names, onMove, onDelete }: { room: Room | null; channel: Channel; categories: Category[]; apps: AppManifest[]; bots: BotInfo[]; names: Map<string, string>; onMove: (categoryId: string | null) => void; onDelete: () => void }) {
  const [name, setName] = useState(channel.name);
  const [topic, setTopic] = useState(channel.topic);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ error: boolean; text: string } | null>(null);
  // Someone else changed it meanwhile: follow, unless this field has unsaved edits.
  const seen = useRef({ name: channel.name, topic: channel.topic });
  useEffect(() => {
    if (name === seen.current.name) setName(channel.name);
    if (topic === seen.current.topic) setTopic(channel.topic);
    seen.current = { name: channel.name, topic: channel.topic };
  }, [channel.name, channel.topic]);

  const cleanName = name.trim();
  const dirty = (cleanName && cleanName !== channel.name) || topic.trim() !== channel.topic;
  const save = async () => {
    if (!room || !dirty) return;
    setBusy(true);
    setNote(null);
    if (cleanName && cleanName !== channel.name) {
      const r = await room.renameChannel(channel.id, cleanName);
      if (!r.ok) {
        setBusy(false);
        return setNote({ error: true, text: r.message });
      }
    }
    if (topic.trim() !== channel.topic) {
      const r = await room.setTopic(channel.id, topic);
      if (!r.ok) {
        setBusy(false);
        return setNote({ error: true, text: r.message });
      }
    }
    setBusy(false);
    setNote({ error: false, text: "Saved." });
  };

  return (
    <form
      className="chset-editor"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="field">
        <label htmlFor={`ch-name-${channel.id}`}>Channel name</label>
        <input id={`ch-name-${channel.id}`} className="input chset-input" maxLength={40} value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, "-"))} />
      </div>
      <div className="field">
        <label htmlFor={`ch-topic-${channel.id}`}>
          Topic <span className="muted" style={{ fontWeight: 400 }}>· shown under the name in the header</span>
        </label>
        <textarea
          id={`ch-topic-${channel.id}`}
          className="input chset-topic-input"
          rows={2}
          maxLength={MAX_TOPIC_LENGTH}
          placeholder="What's this channel for?"
          value={topic}
          onChange={(e) => setTopic(e.target.value.replace(/\n/g, " "))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void save();
            }
          }}
        />
        <span className="chset-count muted">{topic.length}/{MAX_TOPIC_LENGTH}</span>
      </div>
      <div className="chset-editor-row">
        <button type="submit" className="btn btn-small btn-primary" disabled={!dirty || busy}>{busy ? "Saving…" : "Save"}</button>
        {note && <span className={note.error ? "error" : "muted"} role={note.error ? "alert" : "status"} style={{ fontSize: 13 }}>{note.text}</span>}
      </div>
      {apps.length > 0 && <ChannelApps room={room} channel={channel} apps={apps} />}
      {bots.some((b) => b.access.who !== "private") && <ChannelBots room={room} channel={channel} bots={bots.filter((b) => b.access.who !== "private")} names={names} />}
      <div className="chset-editor-row chset-editor-foot">
        <label className="chset-move">
          <span>Move to</span>
          <select
            className="input chset-select"
            value={channel.categoryId ?? ""}
            onChange={(e) => onMove(e.target.value || null)}
          >
            <option value="">No category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>
        <button type="button" className="btn btn-small chset-delete" onClick={onDelete}>Delete channel</button>
      </div>
    </form>
  );
}

/**
 * Channel settings → Bots (docs/bots.md): which bots are in this channel. Owners and admins add or take out any bot
 * (a bot's owner can also add it from its About tab); private bots never join channels.
 */
function ChannelBots({ room, channel, bots, names }: { room: Room | null; channel: Channel; bots: BotInfo[]; names: Map<string, string> }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="chset-apps" role="group" aria-labelledby={`chset-bots-${channel.id}`}>
      <div className="chset-apps-head" id={`chset-bots-${channel.id}`}>Bots <span className="muted" style={{ fontWeight: 400 }}>· who answers and listens in #{channel.name}</span></div>
      {bots.map((b) => {
        const on = b.channels.includes(channel.id);
        return (
          <label key={b.app} className="chset-app">
            <span className="chset-app-icon" aria-hidden="true">{b.avatar}</span>
            <span className="chset-app-text">
              <span className="chset-app-name">{b.name}</span>
              <span className="muted">{b.disabled ? "Switched off" : on ? "In this channel" : "Not here"} · by {names.get(b.ownerId) ?? "someone who left"}</span>
            </span>
            <input
              type="checkbox"
              className="ss-check"
              aria-label={`${b.name} in #${channel.name}`}
              checked={on}
              onChange={async (e) => {
                setError(null);
                const r = await room?.botSettings(b.app, { channelId: channel.id, on: e.target.checked });
                if (r && !r.ok) setError(r.message);
              }}
            />
          </label>
        );
      })}
      {error && <p className="error" role="alert">{error}</p>}
      <p className="setting-help">A bot answers @mentions only in channels it's in (and in its DMs); its other triggers only run here too.</p>
    </div>
  );
}

/**
 * Channel settings → Apps: whether each app may post cards here (its threads follow). On by default. Off, an app's
 * results aren't posted here (the person who used it is told on their private card) and its data cards are refused.
 */
function ChannelApps({ room, channel, apps }: { room: Room | null; channel: Channel; apps: AppManifest[] }) {
  const [error, setError] = useState<string | null>(null);
  const off = new Set(channel.appCardsOff ?? []);
  return (
    <div className="chset-apps" role="group" aria-labelledby={`chset-apps-${channel.id}`}>
      <div className="chset-apps-head" id={`chset-apps-${channel.id}`}>Apps <span className="muted" style={{ fontWeight: 400 }}>· cards they post in #{channel.name}</span></div>
      {apps.map((a) => {
        const on = !off.has(a.slug);
        return (
          <label key={a.slug} className="chset-app">
            <span className="chset-app-icon" aria-hidden="true">{a.icon}</span>
            <span className="chset-app-text">
              <span className="chset-app-name">{a.name}</span>
              <span className="muted">{on ? "Cards show up here" : "No cards here: results are kept quiet"}</span>
            </span>
            <input
              type="checkbox"
              className="ss-check"
              aria-label={`Allow ${a.name} cards in #${channel.name}`}
              checked={on}
              onChange={async (e) => {
                setError(null);
                const r = await room?.setAppCards(channel.id, a.slug, e.target.checked);
                if (r && !r.ok) setError(r.message);
              }}
            />
          </label>
        );
      })}
      {error && <p className="error" role="alert">{error}</p>}
      <p className="setting-help">Even when on, each app posts at most {CARDS_PER_MINUTE} result cards a minute here; more fold into the last one as “+N more”.</p>
    </div>
  );
}
